import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
} from '@angular/core';
import { DsTooltipDirectiveModule, SelectListOption } from '@bmw-ds/components';

import { NationalIdDocumentType } from '../../../customer/model/customer-information.model';
import { GCDM_MAX_LENGTH } from '../../../customer/model/gcdm-constraints';
import { IdentityField } from '../../../customer/model/identity-field.model';
import { FeatureVisibilityService } from '../../../customer/services/feature-visibility.service';
import { InputFieldComponent } from '../../../form/components/input-field/input-field.component';
import { SelectFieldComponent } from '../../../form/components/select-field/select-field.component';
import { useI18n } from '../../../i18n/services/i18n.service';

@Component({
  selector: 'cdf-personal-identification-numbers',
  imports: [InputFieldComponent, SelectFieldComponent, DsTooltipDirectiveModule],
  host: { class: 'd-flex flex-column gap-2x' },
  template: `
    <span class="title-sm">{{ i18n('personalIdentificationNumbers.componentHeader') }}</span>

    <div class="identity-row">
      <cdf-select-field
        id="tax-number-country"
        [(model)]="taxNumberCountry"
        [placeholder]="i18n('placeholder.taxNumberCountry')"
        [options]="availableTaxNumberCountries()"
        [disabled]="disabled()"
        [isClearable]="true"
        [hidden]="featureVisibilityService.isHidden(IdentityField.TAX_NUMBER)"
      />
      <cdf-input-field
        id="tax-number"
        [(model)]="taxNumber"
        [placeholder]="i18n('personalIdentificationNumbers.taxNumber')"
        [displayError]="invalidIdentificationNumberFields().includes(IdentityField.TAX_NUMBER)"
        [errorMessage]="i18n('customerInformationDialog.error.invalidTaxNumber')"
        [disabled]="disabled() || !taxNumberCountry()"
        [maxLength]="gcdmMaxLength.taxNumber"
        [ds-tooltip]="i18n('customerInformationDialog.selectTaxNumberCountryFirstTooltip')"
        [dsTooltipConfig]="{ disabled: !!taxNumberCountry(), origin: 'bottom', showTriggerDelay: 500 }"
        [hidden]="featureVisibilityService.isHidden(IdentityField.TAX_NUMBER)"
      />
    </div>

    <div class="identity-row">
      <cdf-select-field
        id="national-id-document-type"
        [(model)]="nationalIdDocumentType"
        [placeholder]="i18n('placeholder.nationalIdDocumentType')"
        [options]="nationalIdDocumentTypes()"
        [displayError]="invalidIdentificationNumberFields().includes(IdentityField.NATIONAL_ID_DOCUMENT_TYPE)"
        [errorMessage]="i18n('customerInformationDialog.error.emptySelect')"
        [disabled]="disabled()"
        [isClearable]="true"
        [hidden]="featureVisibilityService.isHidden(IdentityField.NATIONAL_ID_DOCUMENT_TYPE)"
      />
      <cdf-input-field
        id="national-id"
        [(model)]="nationalId"
        [placeholder]="i18n('personalIdentificationNumbers.nationalId')"
        [displayError]="invalidIdentificationNumberFields().includes(IdentityField.NATIONAL_ID)"
        [errorMessage]="i18n('customerInformationDialog.error.emptyInput')"
        [disabled]="disabled() || (!nationalIdDocumentType() && !nationalId())"
        [maxLength]="gcdmMaxLength.nationalId"
        [ds-tooltip]="i18n('customerInformationDialog.selectIdTypeFirstTooltip')"
        [dsTooltipConfig]="{
          disabled: !!nationalIdDocumentType() || !!nationalId(),
          origin: 'bottom',
          showTriggerDelay: 500,
        }"
        [hidden]="featureVisibilityService.isHidden(IdentityField.NATIONAL_ID)"
      />
    </div>
  `,
  styleUrl: './personal-identification-numbers.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PersonalIdentificationNumbersComponent {
  protected readonly i18n = useI18n();
  protected readonly featureVisibilityService = inject(FeatureVisibilityService);

  readonly taxNumber = model<string>();
  readonly taxNumberCountry = model<string>();
  readonly nationalId = model<string>();
  readonly nationalIdDocumentType = model<NationalIdDocumentType>();

  readonly availableTaxNumberCountries = input.required<SelectListOption[]>();
  readonly availableNationalIdDocumentTypes = input.required<NationalIdDocumentType[]>();

  readonly disabled = input(false, { transform: booleanAttribute });
  readonly invalidIdentificationNumberFields = input<IdentityField[]>([]);

  private previousNationalIdDocumentType: NationalIdDocumentType | undefined;

  /**
   * Clears the value when the user clears its type, so the pair GCDM requires stays consistent.
   * <p>
   * Only reacts to an actual transition. A customer synced from Retail CRM can carry a national id
   * with no document type, and clearing on every falsy read would wipe it while the dialog is
   * initialising - before the user ever sees it.
   */
  private readonly clearNationalIdOnClearedDocumentType = effect(() => {
    const documentType = this.nationalIdDocumentType();
    if (!documentType && this.previousNationalIdDocumentType) {
      this.nationalId.set(undefined);
    }
    this.previousNationalIdDocumentType = documentType;
  });

  private readonly clearTaxNumberOnClearedTaxNumberCountry = effect(() => {
    if (!this.taxNumberCountry()) {
      this.taxNumber.set(undefined);
    }
  });

  protected readonly nationalIdDocumentTypes = computed(() =>
    this.availableNationalIdDocumentTypes().map((type) => ({
      id: type,
      label: this.i18n.enum(type, 'nationalIdDocumentType'),
    })),
  );

  protected readonly IdentityField = IdentityField;
  protected readonly gcdmMaxLength = GCDM_MAX_LENGTH;
}
